import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Camera, Copy, Eye, Edit, MoreVertical, Link as LinkIcon, QrCode, Download, X, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatEventDate, isFutureDate, isToday, getDaysDifference } from '../../utils/dateUtils.js';
import { eventsService } from '../../services/firestoreService.js';
import ConfirmDialog from '../Common/ConfirmDialog';
import { db } from '../../firebase/config.js';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';

export const EventCard = ({ event, onEdit, onUpdated }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [counts, setCounts] = useState({ participants: null, photos: null });
  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  // Real counts (no campos incrementales)
  useEffect(() => {
    const id = event.eventId || event.id;
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const pq = query(collection(db, 'eventParticipants'), where('eventId', '==', id));
        const pc = await getCountFromServer(pq);
        const fq = query(collection(db, 'photos'), where('eventId', '==', id), where('status', '==', 'approved'));
        const fc = await getCountFromServer(fq);
        if (!cancelled) setCounts({ participants: pc.data().count, photos: fc.data().count });
      } catch (e) {
        // Fallback a los campos si falla
        if (!cancelled) setCounts({ participants: null, photos: null });
      }
    })();
    return () => { cancelled = true; };
  }, [event?.eventId, event?.id]);

  const getStatusText = (status) => (status === 'active' ? 'Activo' : 'Inactivo');

  const getDateDisplay = () => {
    if (!event.date) return 'Fecha no definida';
    
    const date = formatEventDate(event.date);
    const daysDiff = getDaysDifference(event.date);
    
    if (isToday(event.date)) {
      return `Hoy - ${date}`;
    } else if (isFutureDate(event.date)) {
      if (daysDiff === 1) {
        return `Mañana - ${date}`;
      } else if (daysDiff <= 7) {
        return `En ${daysDiff} días - ${date}`;
      } else {
        return date;
      }
    } else {
      return date;
    }
  };

  const copyJoinCode = async () => {
    if (!event.joinCode) return;
    try {
      await navigator.clipboard.writeText(event.joinCode);
      toast.success('Código copiado');
    } catch (_) {
      toast.success(`Código: ${event.joinCode}`);
    }
  };

  const copyInviteLink = async () => {
    if (!event.joinCode) return;
    const url = `https://shot.muchpic.com/shot?code=${event.joinCode}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace de invitación copiado');
    } catch (_) {
      toast.success('No se pudo copiar automáticamente');
    }
    setMenuOpen(false);
  };

  const getQrUrl = () => {
    const target = `https://shot.muchpic.com/shot?code=${encodeURIComponent(event.joinCode || '')}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=420x420&margin=0&data=${encodeURIComponent(target)}`;
  };

  const downloadQr = async () => {
    const url = getQrUrl();
    try {
      // Intentar descargar como blob para forzar descarga incluso cross-origin (si CORS lo permite)
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error('fetch-failed');
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = obj;
      a.download = `qr-${event.joinCode || 'evento'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(obj), 2000);
    } catch (_) {
      // Fallback: usar <a download> directo (algunos navegadores lo respetan) o abrir nueva pestaña
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-${event.joinCode || 'evento'}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        window.open(url, '_blank');
      }
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(event);
    }
  };

  const handleViewDetails = () => {
    // Reutilizamos el modal de edición como vista de detalles
    handleEdit();
  };

  const handleManagePhotos = () => {
    const id = event.eventId || event.id;
    if (id) navigate(`/events/${id}/photos`);
  };

  const handleViewParticipants = () => {
    const id = event.eventId || event.id;
    if (id) navigate(`/events/${id}/participants`);
  };

  const handleManage = () => {
    const id = event.eventId || event.id;
    if (id) navigate(`/events/${id}/manage`);
  };

  const [confirmState, setConfirmState] = useState({ open: false, next: null, loading: false, title: '', message: '', variant: 'warning' });

  const changeStatus = async (next) => {
    const id = event.eventId || event.id;
    if (!id) return;
    try {
      await eventsService.updateEvent(id, { status: next });
      toast.success(`Estado cambiado a ${getStatusText(next)}`);
      if (onUpdated) onUpdated();
    } catch (e) {
      console.error('Error cambiando estado', e);
      toast.error('No se pudo cambiar el estado');
    }
  };

  const askChange = (next) => {
    const info = next === 'active'
      ? { t: 'Activar evento', m: 'El evento se activará y será visible públicamente.' }
      : { t: 'Desactivar evento', m: 'El evento quedará inactivo y no será visible públicamente.' };
    setConfirmState({ open: true, next, loading: false, title: info.t, message: info.m, variant: next === 'active' ? 'warning' : 'danger' });
  };

  const handleConfirm = async () => {
    const next = confirmState.next;
    try {
      setConfirmState(s => ({ ...s, loading: true }));
      await changeStatus(next);
      setConfirmState({ open: false, next: null, loading: false });
    } catch (e) {
      setConfirmState(s => ({ ...s, loading: false }));
    }
  };

  const handleCancel = () => setConfirmState({ open: false, next: null, loading: false });

  const renderStatusActions = () => {
    const s = event.status === 'active' ? 'active' : 'inactive';
    const Btn = ({ onClick, children, title, variant='secondary' }) => {
      const cls = variant === 'primary' ? 'btn-primary text-xs' : (variant === 'danger' ? 'btn-danger text-xs' : 'btn-secondary text-xs');
      return (
        <button onClick={onClick} title={title} className={cls}>
          {children}
        </button>
      );
    };
    if (s === 'active') {
      return (
        <div className="flex flex-wrap gap-2">
          <Btn onClick={() => askChange('inactive')} title="Desactivar evento" variant='danger'>Desactivar</Btn>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap gap-2">
        <Btn onClick={() => askChange('active')} title="Activar evento" variant='primary'>Activar</Btn>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 border border-gray-200 hover:shadow-md transition-shadow relative">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate" title={event.name}>
            {event.name}
          </h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
            <div className="flex items-center min-w-0">
              <Calendar className="h-4 w-4 mr-1" />
              {getDateDisplay()}
            </div>
            <div className="flex items-center min-w-0">
              <MapPin className="h-4 w-4 mr-1" />
              <span className="truncate" title={event.location}>{event.location || 'Ubicación no definida'}</span>
            </div>
            {/* Moderación badge */}
            {(() => {
              const moderation = typeof event?.photoSettings?.moderation === 'boolean'
                ? event.photoSettings.moderation
                : !(event?.settings?.autoApprove === true);
              return (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${moderation ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                  {moderation ? 'Moderación activa' : 'Auto-aprobación'}
                </span>
              );
            })()}
          </div>
        </div>
        <div className="flex items-center gap-1 relative">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
            {getStatusText(event.status)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <Users className="h-4 w-4 mr-2" />
          <span>{(counts.participants ?? event.totalParticipants ?? event.participants ?? 0)} participantes</span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Camera className="h-4 w-4 mr-2" />
          <span>{(counts.photos ?? event.totalPhotos ?? event.photos ?? 0)} fotos</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-900">Código de unión:</span>
          <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
            {event.joinCode || 'Sin código'}
          </span>
        </div>
        <button
          onClick={copyJoinCode}
          disabled={!event.joinCode}
          className="text-indigo-600 hover:text-indigo-700 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          title="Copiar código"
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>

      {/* Acciones del evento simplificadas */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setQrOpen(true)} className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 flex items-center">
            <QrCode className="h-4 w-4 mr-1" />
            Generar QR
          </button>
          <button onClick={copyInviteLink} className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 flex items-center">
            <LinkIcon className="h-4 w-4 mr-1" />
            Copiar enlace
          </button>
        </div>
        <div className="relative flex items-center gap-2" />
      </div>

      {/* Acciones de estado */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 mb-2">Estado: {getStatusText(event.status)}</div>
        <div className="flex items-center justify-between gap-2">
          {renderStatusActions()}
          <button onClick={handleManage} className="btn-secondary text-xs">
            Gestionar
          </button>
          
        </div>
      </div>

      {/* Sin menú de tres puntos en móviles */}

      {/* Modal QR */}
      {qrOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setQrOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-2xl p-5 w-[min(92vw,520px)]">
            <button onClick={() => setQrOpen(false)} className="absolute top-2 right-2 p-1.5 rounded hover:bg-gray-100" aria-label="Cerrar">
              <X className="h-5 w-5 text-gray-600" />
            </button>
            <h3 className="text-lg font-semibold mb-3">QR para unirse</h3>
            <div className="text-sm text-gray-600 mb-4 break-all">
              https://shot.muchpic.com/shot?code={event.joinCode}
            </div>
            <div className="flex items-center justify-center">
              <img
                src={getQrUrl()}
                alt={`QR para ${event.joinCode}`}
                className="rounded-md border"
                style={{ width: 320, height: 320 }}
              />
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={copyInviteLink} className="btn-secondary text-sm">Copiar enlace</button>
              <button onClick={downloadQr} className="btn-primary text-sm flex items-center">
                <Download className="h-4 w-4 mr-1" /> Descargar QR
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        loading={confirmState.loading}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmText="Confirmar"
      />
    </div>
  );
};
